import React, { useEffect, useRef } from "react";
import { User, Bot } from "lucide-react";
import VoiceMessage from "./VoiceMessage"; // ✅ import this

export default function ChatWindow({ messages, loading }) {
  const bottomRef = useRef();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const formatAIMessage = (text) => {
    if (!text) return "";

    // Highlight section headers like "VEG STARTERS"
    const withHeadings = text.replace(
      /^([A-Z\s&–\-]+)$/gm,
      (match) => `<div class="font-semibold text-purple-700 mt-2 mb-1">${match.trim()}</div>`
    );

    // Replace "- Item - ₹Price" pattern with clean list format
    const withListItems = withHeadings.replace(
      /- (.*?) - ₹(\d+)/g,
      `<div class="flex justify-between"><span>$1</span><span class="font-medium">₹$2</span></div>`
    );

    // Keep line breaks
    return withListItems.replace(/\n/g, "<br/>");
  };

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 bg-gradient-to-br from-white to-purple-50">
      {messages.length === 0 && (
        <p className="text-center text-gray-400 mt-10">Start a conversation 💬</p>
      )}

      {messages.map((m, i) => (
        <div
          key={i}
          className={`flex items-end gap-2 mb-3 ${m.self ? "justify-end" : "justify-start"}`}
        >
          {/* 🧠 AI Avatar */}
          {!m.self && (
            <div className="p-1.5 bg-gray-200 rounded-full border border-gray-300 shadow-sm">
              <Bot size={20} className="text-gray-700" />
            </div>
          )}

          {/* 💬 Chat Bubble */}
          <div
            className={`max-w-[75%] rounded-2xl shadow-sm ${
              m.self
                ? "bg-purple-500 text-white rounded-br-none"
                : "bg-gray-100 text-gray-800 rounded-bl-none"
            }`}
          >
            {m.type === "audio" ? (
              // 🎧 Voice Message Bubble
              <div className="px-3 py-2">
                <VoiceMessage src={m.url} isSelf={m.self} />
              </div>
            ) : (
              // 📝 Text Message Bubble
              m.text && (
                <div
                  className="px-4 py-2 whitespace-pre-line text-sm"
                  dangerouslySetInnerHTML={{
                    __html: formatAIMessage(m.text),
                  }}
                />
              )
            )}
          </div>

          {/* 👤 User Avatar */}
          {m.self && (
            <div className="p-1.5 bg-purple-200 rounded-full border border-purple-300 shadow-sm">
              <User size={20} className="text-purple-700" />
            </div>
          )}
        </div>
      ))}

      {/* 🕐 Loading Indicator */}
      {loading && (
        <div className="flex items-center gap-2 text-gray-500 text-sm pl-2">
          <div className="flex gap-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-150"></div>
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-300"></div>
          </div>
          AI is typing...
        </div>
      )}

      <div ref={bottomRef}></div>
    </div>
  );
}
